import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect, useRef } from "react";
import {
  User,
  Plus,
  Mail,
  Phone,
  Building2,
  Briefcase,
  FileText,
  Loader2,
} from "lucide-react";
import "flag-icons/css/flag-icons.min.css";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { countries, countryCodeMap } from "@/data/countries";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { CustomerStatus } from "@/types/crm";
import { useToast } from "@/hooks/use-toast";
import { companyAPI, CreateCompanyData } from "@/services/companyService";
import { useAuth } from "@/hooks/useAuth";

interface CreateContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contact: any) => Promise<void> | void;
  companies?: any[];
  existingContact?: any;
  customerStatuses?: CustomerStatus[];
  existingContacts?: any[];
  defaultCompanyId?: string;
  defaultCompanyName?: string;
}

export function CreateContactModal({
  isOpen,
  onClose,
  onSave,
  companies = [],
  existingContact,
  customerStatuses = [],
  existingContacts = [],
  defaultCompanyId,
  defaultCompanyName,
}: CreateContactModalProps) {
  const { toast } = useToast();
  const { company } = useAuth();

  useEffect(() => {
    if (isOpen && !existingContact && defaultCompanyId) {
      setFormData((prev) => ({
        ...prev,
        companyId: defaultCompanyId,
      }));
    }
  }, [isOpen, existingContact, defaultCompanyId]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "",
    position: "",
    companyId: "",
    linkedinUrl: "",
    notes: "",
    statusId: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [localCompanies, setLocalCompanies] = useState<any[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const fetchCompanies = async () => {
    if (!company?.slug) return;
    setIsLoadingCompanies(true);
    try {
      const response = await companyAPI.getCompanies({
        slug: company.slug,
        limit: 1000,
      });
      if (response.data) {
        setLocalCompanies(response.data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      countryCode: "",
      position: "",
      companyId: "",
      linkedinUrl: "",
      notes: "",
      statusId: "",
    });
    setErrors({});
    setSearchTerm("");
    setShowDropdown(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen, company?.slug]);

  useEffect(() => {
    if (existingContact) {
      let cleanPhone = existingContact.phone || "";
      let code = existingContact.countryCode || "";

      if (code && cleanPhone.startsWith(code)) {
        cleanPhone = cleanPhone.substring(code.length).trim();
      } else if (!code && cleanPhone.startsWith("+")) {
        const matchedCountry = countries.find((c) =>
          cleanPhone.startsWith(c.code),
        );
        if (matchedCountry) {
          code = matchedCountry.code;
          cleanPhone = cleanPhone.substring(code.length).trim();
        }
      }

      setFormData({
        firstName: existingContact.firstName || "",
        lastName: existingContact.lastName || "",
        email: existingContact.email || "",
        phone: cleanPhone,
        countryCode: code,
        position: existingContact.position || "",
        companyId: existingContact.client_company?.id || existingContact.client_company_id || existingContact.company?.id || existingContact.companyId || "",
        linkedinUrl: existingContact.linkedinUrl || "",
        notes: existingContact.notes || "",
        statusId: existingContact.statusId || "",
      });

      const country = countries.find((c) => c.code === code);
      setSearchTerm(country ? `${country.name} (${country.code})` : code || "");
    } else if (!isOpen) {
      resetForm();
    }
  }, [existingContact, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim())
      newErrors.firstName = "El nombre es obligatorio";
    if (formData.email.trim()) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Ingrese un email válido";
      } else {
        const isDuplicate = existingContacts.some(
          (contact) =>
            contact.email &&
            contact.email.toLowerCase() === formData.email.toLowerCase() &&
            contact.id !== existingContact?.id,
        );
        if (isDuplicate) {
          newErrors.email = "Ya existe un contacto con este email";
        }
      }
    }
    if (formData.phone.trim() && formData.countryCode) {
      const phoneRegex = /^[+]?[\d\s\-()]+$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = "Formato no válido";
      }
    } else if (formData.phone.trim() && !formData.countryCode) {
      newErrors.countryCode = "Seleccione un país para el teléfono";
    }

    if (!formData.companyId.trim())
      newErrors.companyId = "Seleccione una empresa";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa todos los campos obligatorios",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const selectedCompany = localCompanies.find(
      (c) => c.id === formData.companyId,
    );

    let finalStatusId = formData.statusId;
    if (!finalStatusId && customerStatuses.length > 0) {
      const activeStatus = customerStatuses.find((s) =>
        s.name.toLowerCase().includes("activo"),
      );
      finalStatusId = activeStatus ? activeStatus.id : customerStatuses[0].id;
    }

    const selectedStatus = customerStatuses.find((s) => s.id === finalStatusId);

    const fullName = `${formData.firstName} ${formData.lastName}`.trim();

    const selectedCountryData = countries.find(
      (c) => c.code === formData.countryCode,
    );
    const formattedPhone = selectedCountryData
      ? `${selectedCountryData.code} ${formData.phone}`
      : formData.phone;

    const newContact = {
      id: existingContact?.id || Date.now().toString(),
      firstName: formData.firstName,
      lastName: formData.lastName,
      name: fullName,
      email: formData.email.trim() || null,
      phone: formData.phone.trim() ? formattedPhone : null,
      countryCode: formData.countryCode || null,
      position: formData.position.trim() || null,
      companyId: formData.companyId || null,
      client_company_id: formData.companyId || null,
      contact_status_id: finalStatusId,
      statusId: finalStatusId,
      linkedinUrl: formData.linkedinUrl.trim() || null,
      notes: formData.notes.trim() || null,
      company: selectedCompany,
      status: selectedStatus?.name,
      opportunities: existingContact?.opportunities || [],
      activities: existingContact?.activities || [],
      attachments: [],
      createdAt: existingContact?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setIsLoading(true);
    try {
      await onSave(newContact);
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error al guardar contacto:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCountryInputChange = (value: string) => {
    setSearchTerm(value);
    setShowDropdown(true);
  };

  const filteredCountries = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.code.includes(searchTerm),
  );

  const handleSelectCountry = (country: (typeof countries)[0]) => {
    setFormData((prev) => ({ ...prev, countryCode: country.code }));
    setSearchTerm(`${country.name} (${country.code})`);
    setShowDropdown(false);
  };

  const handleCreateCompany = async (companyData: CreateCompanyData) => {
    try {
      const response = await companyAPI.createCompany(companyData);
      await fetchCompanies();
      if (response.data?.id) {
        setFormData((prev) => ({ ...prev, companyId: response.data.id }));
      }

      toast({
        title: "Empresa creada",
        description: "La empresa ha sido creada exitosamente",
      });

      setIsCompanyModalOpen(false);
    } catch (error: any) {
      if (
        error.message?.includes("Error al configurar") ||
        error.message?.includes("estructura") ||
        error.message?.includes("exitosamente") ||
        error.message?.includes("creada") ||
        error.message?.includes("success")
      ) {
        try {
          await fetchCompanies();
          toast({
            title: "Empresa creada",
            description: "La empresa ha sido creada exitosamente",
          });
          setIsCompanyModalOpen(false);
          return;
        } catch (refreshError) {
          console.error("Error al refrescar:", refreshError);
        }
      }

      toast({
        title: "Error",
        description: error.message || "No se pudo crear la empresa",
        variant: "destructive",
      });
      throw error;
    }
  };

  const selectedCountry = countries.find(
    (c) => c.code === formData.countryCode,
  );

  const isEditing = !!existingContact && !!existingContact.id && !existingContact.companyId;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-hidden p-0 custom-scrollbar rounded-[24px] border-slate-100 flex flex-col">
          <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">

            {/* Header */}
            <div className="bg-white border-b border-slate-100 p-4 sm:px-6 sm:py-4 z-10 relative flex-shrink-0">
              <DialogHeader>
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100/80 p-2.5 rounded-xl">
                    <User className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                      {existingContact?.id ? "Editar Contacto" : "Nuevo Contacto"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500 mt-0.5">
                      {existingContact?.id ? "Modifica los datos del contacto" : "Registra un nuevo contacto"}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
              <div className="space-y-8">

                {/* Sección: Información Personal */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <User className="h-5 w-5 text-indigo-500" />
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">
                      Información Personal
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nombre */}
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                        Nombre <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleChange("firstName", e.target.value)}
                          placeholder="Juan"
                          className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                            errors.firstName ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                          }`}
                        />
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                      {errors.firstName && (
                        <p className="text-red-500 text-sm flex items-center">
                          <span className="mr-1">•</span> {errors.firstName}
                        </p>
                      )}
                    </div>

                    {/* Apellido */}
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                        Apellido
                      </Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleChange("lastName", e.target.value)}
                        placeholder="Pérez"
                        className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                        Email
                      </Label>
                      <div className="relative">
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          placeholder="juan.perez@empresa.com"
                          className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                            errors.email ? "border-red-500 focus-visible:ring-red-500 bg-red-50/50" : ""
                          }`}
                        />
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-sm flex items-center">
                          <span className="mr-1">•</span> {errors.email}
                        </p>
                      )}
                    </div>

                    {/* Cargo */}
                    <div className="space-y-2">
                      <Label htmlFor="position" className="text-sm font-medium text-slate-700">
                        Cargo
                      </Label>
                      <div className="relative">
                        <Input
                          id="position"
                          value={formData.position}
                          onChange={(e) => handleChange("position", e.target.value)}
                          placeholder="Gerente de Ventas"
                          className="h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm"
                        />
                        <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sección: Contacto */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-5 w-5 text-indigo-500" />
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">
                      Contacto y Empresa
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Teléfono */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                        Teléfono
                      </Label>
                      <div className="flex gap-2 relative">
                        <div className="relative w-44">
                          <Input
                            ref={inputRef}
                            value={searchTerm}
                            onChange={(e) => handleCountryInputChange(e.target.value)}
                            placeholder="País o código"
                            className="h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm text-sm"
                          />
                          {showDropdown && (
                            <div className="absolute z-50 bg-white border border-slate-200 mt-1 max-h-56 overflow-y-auto w-60 shadow-lg rounded-xl">
                              {filteredCountries.map((c) => (
                                <div
                                  key={c.code}
                                  className="cursor-pointer px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                                  onClick={() => handleSelectCountry(c)}
                                >
                                  <span className={`fi fi-${c.flagCode} w-5 h-4 rounded`}></span>
                                  <span className="text-slate-700">{c.name}</span>
                                  <span className="text-slate-400 ml-auto">{c.code}</span>
                                </div>
                              ))}
                              {filteredCountries.length === 0 && (
                                <div className="px-3 py-2 text-slate-400 text-sm">
                                  No se encontró ningún país
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="relative flex-1">
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) =>
                              handleChange("phone", e.target.value.replace(/\D/g, ""))
                            }
                            placeholder={selectedCountry ? selectedCountry.example : "Número"}
                            className={`h-10 pl-9 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm ${
                              errors.phone ? "border-red-500 bg-red-50/50" : ""
                            }`}
                          />
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                      {errors.countryCode && (
                        <p className="text-red-500 text-sm flex items-center">
                          <span className="mr-1">•</span> {errors.countryCode}
                        </p>
                      )}
                      {errors.phone && (
                        <p className="text-red-500 text-sm flex items-center">
                          <span className="mr-1">•</span> {errors.phone}
                        </p>
                      )}
                    </div>

                    {/* Empresa */}
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-sm font-medium text-slate-700">
                        Empresa <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          onValueChange={(value) => handleChange("companyId", value)}
                          value={formData.companyId}
                          disabled={isLoadingCompanies}
                        >
                          <SelectTrigger
                            className={`flex-1 h-10 rounded-lg bg-white border-slate-200 hover:border-slate-300 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm ${
                              errors.companyId ? "border-red-500 bg-red-50/50" : ""
                            }`}
                          >
                            <SelectValue
                              placeholder={
                                isLoadingCompanies
                                  ? "Cargando empresas..."
                                  : "Seleccionar empresa"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(localCompanies) &&
                              localCompanies.map((company) => (
                                <SelectItem key={company.id} value={company.id}>
                                  {company.name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setIsCompanyModalOpen(true)}
                          className="h-10 w-10 rounded-lg border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 shrink-0 shadow-sm"
                          title="Crear nueva empresa"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {errors.companyId && (
                        <p className="text-red-500 text-sm flex items-center">
                          <span className="mr-1">•</span> {errors.companyId}
                        </p>
                      )}
                    </div>

                  </div>
                </div>

                {/* Sección: Notas */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-5 w-5 text-indigo-500" />
                    <h3 className="text-base font-bold text-slate-800 tracking-tight">
                      Notas
                    </h3>
                  </div>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange("notes", e.target.value)}
                    placeholder="Información adicional sobre el contacto..."
                    rows={3}
                    className="rounded-lg bg-white border-slate-200 hover:border-slate-300 focus-visible:ring-indigo-500 focus-visible:border-indigo-500 transition-all shadow-sm resize-none"
                  />
                </div>

              </div>
            </div>

            {/* Footer */}
            <DialogFooter className="bg-slate-50/50 border-t border-slate-100 p-5 px-6 rounded-b-[24px] flex-shrink-0 z-10">
              <div className="flex w-full justify-between items-center">
                <div className="text-xs text-slate-500 font-medium">
                  <span className="text-red-500">*</span> Campos obligatorios
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onClose}
                    disabled={isLoading}
                    className="px-6 h-10 rounded-lg border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="px-6 h-10 rounded-lg bg-slate-900 hover:bg-black text-white font-medium shadow-sm shadow-slate-900/20"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-2" />
                        Procesando...
                      </>
                    ) : existingContact?.id ? (
                      "Actualizar Contacto"
                    ) : (
                      "Crear Contacto"
                    )}
                  </Button>
                </div>
              </div>
            </DialogFooter>

          </form>
        </DialogContent>
      </Dialog>

      <CreateCompanyModal
        isOpen={isCompanyModalOpen}
        onClose={() => setIsCompanyModalOpen(false)}
        onSave={handleCreateCompany}
        companySlug={company?.slug || ""}
      />
    </>
  );
}
